import parameters from './qrParameters.json';
// QR Model 2, byte mode, medium correction. Input is an ASCII-encoded profile URL.
// The standard geometry/RS block table is shared by conforming implementations.
export function profileUrl(nickname, visible, origin) {
  if (!visible) return '';
  const base = new URL(origin);
  if (
    base.protocol !== 'https:' ||
    !['postpop.kr', 'sandbox.postpop.kr'].includes(base.hostname) ||
    base.port ||
    base.username ||
    base.password
  )
    throw Error('Invalid profile host');
  const url = `${base.origin}/users/${encodeURIComponent(nickname)}`;
  if (!nickname || new TextEncoder().encode(url).length > 1500) throw Error('Invalid profile URL');
  return url;
}
function multiply(a, b) {
  let r = 0;
  while (b) {
    if (b & 1) r ^= a;
    b >>= 1;
    a <<= 1;
    if (a & 256) a ^= 0x11d;
  }
  return r;
}
function parity(bytes, count) {
  let poly = [1];
  for (let i = 0, root = 1; i < count; i++, root = multiply(root, 2)) {
    const next = Array(poly.length + 1).fill(0);
    poly.forEach((v, j) => {
      next[j] ^= v;
      next[j + 1] ^= multiply(v, root);
    });
    poly = next;
  }
  const remainder = [...bytes, ...Array(count).fill(0)];
  for (let i = 0; i < bytes.length; i++) {
    const factor = remainder[i];
    for (let j = 0; j < poly.length; j++) remainder[i + j] ^= multiply(poly[j], factor);
  }
  return remainder.slice(-count);
}
const bit = (v, n) => ((v >>> n) & 1) !== 0;
function bch(value, polynomial, shift) {
  let rest = value << shift;
  const degree = (n) => 31 - Math.clz32(n);
  while (rest && degree(rest) >= degree(polynomial))
    rest ^= polynomial << (degree(rest) - degree(polynomial));
  return (value << shift) | rest;
}
function penalty(m) {
  const n = m.length;
  let score = 0,
    dark = 0;
  for (let direction = 0; direction < 2; direction++)
    for (let a = 0; a < n; a++) {
      let run = 0,
        previous = null;
      const line = [];
      for (let b = 0; b < n; b++) {
        const v = direction ? m[b][a] : m[a][b];
        line.push(v ? '1' : '0');
        if (v === previous) run++;
        else {
          if (run >= 5) score += run - 2;
          run = 1;
          previous = v;
        }
        if (!direction && v) dark++;
      }
      if (run >= 5) score += run - 2;
      const s = line.join('');
      for (let k = 0; k <= n - 11; k++)
        if (['00001011101', '10111010000'].includes(s.slice(k, k + 11))) score += 40;
    }
  for (let y = 0; y < n - 1; y++)
    for (let x = 0; x < n - 1; x++)
      if (m[y][x] === m[y][x + 1] && m[y][x] === m[y + 1][x] && m[y][x] === m[y + 1][x + 1])
        score += 3;
  return score + 10 * Math.floor(Math.abs((dark * 100) / (n * n) - 50) / 5);
}
export function qrMatrix(text, forcedMask) {
  if (typeof text !== 'string' || !/^[\x20-\x7e]+$/.test(text) || text.length > 1500)
    throw Error('QR input must be encoded ASCII');
  const bytes = Array.from(text, (c) => c.charCodeAt(0));
  let version = 1;
  while (
    version <= 40 &&
    4 + (version < 10 ? 8 : 16) + bytes.length * 8 >
      parameters[version - 1].data.reduce((a, b) => a + b, 0) * 8
  )
    version++;
  if (version > 40) throw Error('QR capacity exceeded');
  const spec = parameters[version - 1],
    capacity = spec.data.reduce((a, b) => a + b, 0) * 8,
    bits = [];
  const append = (v, count) => {
    for (let i = count - 1; i >= 0; i--) bits.push(bit(v, i));
  };
  append(4, 4);
  append(bytes.length, version < 10 ? 8 : 16);
  bytes.forEach((v) => append(v, 8));
  append(0, Math.min(4, capacity - bits.length));
  while (bits.length % 8) bits.push(false);
  let padding = 0;
  while (bits.length < capacity) append(padding++ % 2 ? 17 : 236, 8);
  const data = Array.from({ length: bits.length / 8 }, (_, i) =>
    bits.slice(i * 8, i * 8 + 8).reduce((v, b) => v * 2 + Number(b), 0),
  );
  let offset = 0;
  const blocks = spec.data.map((size) => {
    const r = data.slice(offset, offset + size);
    offset += size;
    return { data: r, ecc: parity(r, spec.ecc) };
  });
  const encoded = [];
  for (const key of ['data', 'ecc'])
    for (let i = 0; i < Math.max(...blocks.map((b) => b[key].length)); i++)
      for (const b of blocks) if (i < b[key].length) encoded.push(b[key][i]);
  const size = 17 + 4 * version,
    base = Array.from({ length: size }, () => Array(size).fill(null));
  const finder = (x, y) => {
    for (let dy = -1; dy <= 7; dy++)
      for (let dx = -1; dx <= 7; dx++) {
        const xx = x + dx,
          yy = y + dy;
        if (xx < 0 || yy < 0 || xx >= size || yy >= size) continue;
        base[yy][xx] =
          dx >= 0 &&
          dx <= 6 &&
          dy >= 0 &&
          dy <= 6 &&
          (dx === 0 ||
            dx === 6 ||
            dy === 0 ||
            dy === 6 ||
            (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4));
      }
  };
  finder(0, 0);
  finder(size - 7, 0);
  finder(0, size - 7);
  for (const y of spec.align)
    for (const x of spec.align) {
      if (base[y][x] !== null) continue;
      for (let dy = -2; dy <= 2; dy++)
        for (let dx = -2; dx <= 2; dx++)
          base[y + dy][x + dx] = Math.max(Math.abs(dx), Math.abs(dy)) !== 1;
    }
  for (let i = 8; i < size - 8; i++) {
    if (base[6][i] === null) base[6][i] = i % 2 === 0;
    if (base[i][6] === null) base[i][6] = i % 2 === 0;
  }
  if (version >= 7) {
    const info = bch(version, 0x1f25, 12);
    for (let i = 0; i < 18; i++) {
      base[Math.floor(i / 3)][size - 11 + (i % 3)] = bit(info, i);
      base[size - 11 + (i % 3)][Math.floor(i / 3)] = bit(info, i);
    }
  }
  const masks = [
    (x, y) => (x + y) % 2 === 0,
    (x, y) => y % 2 === 0,
    (x, y) => x % 3 === 0,
    (x, y) => (x + y) % 3 === 0,
    (x, y) => (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0,
    (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
    (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
    (x, y) => (((x * y) % 3) + ((x + y) % 2)) % 2 === 0,
  ];
  if (
    forcedMask !== undefined &&
    (!Number.isInteger(forcedMask) || forcedMask < 0 || forcedMask > 7)
  )
    throw Error('Invalid mask');
  const build = (mask) => {
    const m = base.map((r) => [...r]),
      format = bch(mask, 0x537, 10) ^ 0x5412;
    for (let i = 0; i < 15; i++) {
      m[i < 6 ? i : i < 8 ? i + 1 : size - 15 + i][8] = bit(format, i);
      m[8][i < 8 ? size - i - 1 : i === 8 ? 7 : 15 - i - 1] = bit(format, i);
    }
    m[size - 8][8] = true;
    let position = 0,
      up = true;
    for (let x = size - 1; x > 0; x -= 2) {
      if (x === 6) x--;
      for (let i = 0; i < size; i++) {
        const y = up ? size - 1 - i : i;
        for (let col = x; col >= x - 1; col--)
          if (m[y][col] === null) {
            const value =
              position < encoded.length * 8
                ? bit(encoded[Math.floor(position / 8)], 7 - (position % 8))
                : false;
            position++;
            m[y][col] = value !== masks[mask](col, y);
          }
      }
      up = !up;
    }
    return m;
  };
  if (forcedMask !== undefined) return build(forcedMask);
  return Array.from({ length: 8 }, (_, i) => build(i)).sort((a, b) => penalty(a) - penalty(b))[0];
}
